import { useState } from 'react';

import styled from '@emotion/styled';
import { Trans } from '@lingui/react/macro';
import { BatchPredictionSharp, InfoOutlined } from '@mui/icons-material';
import Refresh from '@mui/icons-material/Refresh';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
} from '@mui/material';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router';

import { VideaButton } from '../../../common-components/elements/VideaButton/VideaButton';
import { RefreshImageIcon } from '../../../common-components/elements/VideaIcons/VideaIcons';
import { useLatestImagesV2ToV1 } from '../../../react-query/useLatestImagesv2';
import { usePreferences } from '../../../react-query/usePreferences';
import {
  blueBlack400,
  blueBlack650,
  blueUltramarine300,
  orange700,
} from '../../../theme/videa-style-tokens';
import {
  useRerunAi,
  useRerunAiForNewCariesModel,
} from '../../../utils/hooks/useRerunAi';
import { mixpanelEvents, useTrackEvent } from '../../../utils/mixpanel';
import AnnotatorConstants from '../../annotator/annotator.constants';
import { useTrackRerunAnalysisClick } from '../multiFileViewer/utils';
import type { ImageObject } from '../types';

interface ReRunAIProps {
  estimatedVisitId: number;
  images: { imageId: string; rotation: number }[];
  selectedPatientId: string;
  isForSharpening?: boolean;
}

interface ReRunAIForCariesProps {
  estimatedVisitId: number;
  selectedPatientId: string;
}

const RerunAiIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: nowrap;
  background-color: ${blueBlack650};
  padding: 0.375em 0 0.5em 1.25em;

  .MuiChip-root {
    color: ${orange700};
  }
  .MuiChip-root:hover {
    color: ${orange700};
    .MuiChip-labelMedium {
      color: ${orange700};
    }
  }
  .warning.MuiSvgIcon-root {
    fill: ${orange700};
  }
  .info.MuiSvgIcon-root {
    fill: ${blueBlack400};
    font-size: 1em;
    transform: translateX(-0.5em);
  }
  .MuiButton-root {
    background-color: ${blueUltramarine300};
  }
`;

const RerunButton = styled(VideaButton)`
  color: white;
  gap: 0.5em;
  &.rerun-ai {
    &.disabled {
      background-color: ${blueBlack650};
      color: white;
      opacity: 0.5;
      padding-left: 2em;
      position: relative;

      &:after {
        content: '';
        position: absolute;
        width: 1em;
        height: 1em;
        top: 0.25em;
        left: 0.25em;
        margin: auto;
        border: 0.25em solid transparent;
        border-top-color: white;
        border-radius: 50%;
        animation: spinnah 1s ease infinite;
      }
    }
  }

  @keyframes spinnah {
    from {
      transform: rotate(0turn);
    }
    to {
      transform: rotate(1turn);
    }
  }
`;

const ImageSharpeningWarning = styled.div`
  display: flex;
  flex-direction: row;
  height: 2em;
  .warning {
    width: 8em;
    font-size: 0.875em;
    border-radius: 0.375em;
    padding: 0.25em 0.5em;
    color: ${orange700};
    display: flex;
    align-items: center;
    white-space: normal;
  }
`;

const RerunAiButton = ({
  estimatedVisitId,
  images,
  selectedPatientId,
  isForSharpening,
}: ReRunAIProps) => {
  const { mutateAsync, isLoading } = useRerunAi(images, estimatedVisitId);
  const dispatch = useDispatch();
  const { trackRerunAnalysisClick } = useTrackRerunAnalysisClick();

  const handleRerun = () => {
    if (isLoading) return;

    mutateAsync().then(() => {
      trackRerunAnalysisClick({
        numberOfImages: images.length,
        patientId: selectedPatientId,
        rerunSource: 'sidebar',
      });

      dispatch({
        type: AnnotatorConstants.RESET_IMAGES_FOR_PATIENT,
        response: {
          data: { patientId: selectedPatientId },
        },
      });
      window.location.reload();
    });
  };

  const classes = classNames('rerun-ai', { disabled: isLoading });

  const getButtonText = () => {
    if (isForSharpening) {
      return isLoading ? <Trans>Rerunning</Trans> : <Trans>Rerun</Trans>;
    }
    return isLoading ? <Trans>Rerunning AI</Trans> : <Trans>Rerun AI</Trans>;
  };

  return (
    <RerunButton
      variant="outlined"
      className={classes}
      size="small"
      onClick={handleRerun}
      disabled={isLoading}
      aria-label="Rerun AI Analysis"
    >
      {!isLoading && <Refresh />}
      {getButtonText()}
    </RerunButton>
  );
};

const RerunAiButtonCaries = ({
  estimatedVisitId,
  selectedPatientId,
}: ReRunAIForCariesProps) => {
  const { mutateAsync, isLoading } =
    useRerunAiForNewCariesModel(estimatedVisitId);
  const dispatch = useDispatch();
  const { trackEvent } = useTrackEvent();

  const handleRerun = () => {
    if (isLoading) return;

    mutateAsync().then(() => {
      trackEvent(mixpanelEvents.rerunAnalysis, {
        patientId: selectedPatientId,
        rerunSource: 'sidebar',
      });

      dispatch({
        type: AnnotatorConstants.RESET_IMAGES_FOR_PATIENT,
        response: {
          data: { patientId: selectedPatientId },
        },
      });
      window.location.reload();
    });
  };

  const classes = classNames('rerun-ai', { disabled: isLoading });

  return (
    <RerunButton
      variant="outlined"
      className={classes}
      size="small"
      onClick={handleRerun}
      disabled={isLoading}
      aria-label="Rerun AI for Latest Caries Model"
    >
      {!isLoading && <Refresh />}
      {isLoading ? 'Rerunning AI' : 'Rerun AI'}
    </RerunButton>
  );
};

export const RerunAiForImageSharpening = ({
  estimatedVisitId,
  images,
  selectedPatientId,
}: {
  estimatedVisitId: number;
  images: ImageObject[];
  selectedPatientId: string;
}) => {
  const { data } = usePreferences();
  const imageAdjustmentVersion = data?.adjustmentVersion;

  const isMatchingAdjustmentVersion = (sharpenedPath?: string) => {
    if (!sharpenedPath || !imageAdjustmentVersion) {
      return true;
    }
    return sharpenedPath.search(imageAdjustmentVersion) === -1 ? false : true;
  };

  const shouldRerunAi = images?.some(
    (image) => !isMatchingAdjustmentVersion(image?.sharpenedPath),
  );

  return (
    shouldRerunAi && (
      <Tooltip title="This image does not have the latest image sharpening filter applied.">
        <ImageSharpeningWarning>
          <RefreshImageIcon />
          <div className="warning">
            <Trans>Newer Image Filter Available</Trans>
          </div>
          <RerunAi
            estimatedVisitId={estimatedVisitId}
            images={images}
            selectedPatientId={selectedPatientId}
            isInRBLMenu={true}
          />
        </ImageSharpeningWarning>
      </Tooltip>
    )
  );
};

export const RerunAiForNewCariesModel = ({
  estimatedVisitId,
  isUsingLatestModel,
}: {
  estimatedVisitId?: number;
  isUsingLatestModel?: boolean;
}) => {
  const { id: selectedPatientId } = useParams<{
    id: string;
  }>();

  if (isUsingLatestModel === undefined) {
    return null;
  }

  if (!isUsingLatestModel) {
    return (
      estimatedVisitId && (
        <Tooltip title="This image does not have the latest AI model.">
          <ImageSharpeningWarning>
            <RefreshImageIcon />
            <div className="warning">
              <Trans>Newer Caries Model Available</Trans>
            </div>
            <RerunAiButtonCaries
              estimatedVisitId={estimatedVisitId}
              selectedPatientId={selectedPatientId}
            />
          </ImageSharpeningWarning>
        </Tooltip>
      )
    );
  }
};

export const RerunAi = (props: {
  estimatedVisitId?: number | null;
  images?: ImageObject[];
  selectedPatientId: string;
  isInRBLMenu?: boolean;
  isForSharpening?: boolean;
}) => {
  const {
    estimatedVisitId,
    images: sortedAndFilteredImages,
    selectedPatientId,
    isInRBLMenu,
    isForSharpening,
  } = props;

  if (!estimatedVisitId || !sortedAndFilteredImages) {
    return null;
  }

  const reRunImages = sortedAndFilteredImages
    .map((image: ImageObject) => {
      return {
        imageId: image.imageId,
        rotation: 0,
      };
    })
    .filter((image: { imageId: string }) => !!image.imageId);

  return !isInRBLMenu ? (
    <RerunAiIndicator>
      <div className="vai-text-orange-400 vai-font-semibold vai-flex vai-items-center vai-gap-1 vai-mr-4">
        <BatchPredictionSharp className="warning vai-w-5 vai-h-5" />
        {isForSharpening ? (
          <Trans>Previous Filter</Trans>
        ) : (
          <Trans>Previous AI Model</Trans>
        )}
      </div>

      <Tooltip
        title={
          isForSharpening ? (
            <Trans>
              This image does not have the latest image sharpening filter
              applied.
            </Trans>
          ) : (
            <Trans>
              AI results are not from latest model. Please rerun AI for latest
              analysis.
            </Trans>
          )
        }
      >
        <InfoOutlined className="info" />
      </Tooltip>
      <RerunAiButton
        estimatedVisitId={estimatedVisitId}
        images={reRunImages}
        selectedPatientId={selectedPatientId}
        isForSharpening={isForSharpening}
      />
    </RerunAiIndicator>
  ) : (
    <RerunAiButton
      estimatedVisitId={estimatedVisitId}
      images={reRunImages}
      selectedPatientId={selectedPatientId}
      isForSharpening={isForSharpening}
    />
  );
};

export const AIHasReran = () => {
  const { id: selectedPatientId } = useParams<{ id: string }>();
  const history = useHistory();
  const splitCurrentUrl = history.location.pathname.split('/');
  const videaImageId = splitCurrentUrl.length >= 4 && splitCurrentUrl.pop();
  const { data } = useLatestImagesV2ToV1({
    videaPatientId: selectedPatientId,
  });
  const analyses = videaImageId
    ? data?.analyses?.filter(
        (analysis) => analysis.mongoImageId === videaImageId,
      )
    : data?.analyses;
  const hasRanWithVDACaries = !!data?.analyzedImages.some(
    (image) => image.models.caries?.type === 'VIDEA_DENTAL_ASSIST_CARIES',
  );
  const hasReranAi = !!analyses?.every((analysis) =>
    analysis.analysesHistory?.some(
      (historicAnalysis) => historicAnalysis.type === 'rerun',
    ),
  );

  if (!hasReranAi || !hasRanWithVDACaries) {
    return null;
  }

  let previousValidations: string[] | undefined = [];
  if (videaImageId) {
    previousValidations = analyses?.reduce((acc, current) => {
      current.analysesHistory?.forEach((previousValidation) => {
        if (
          previousValidation.data?.status &&
          previousValidation.type === 'previousValidation'
        ) {
          acc.push(
            `Tooth ${previousValidation.data.toothId} - ${previousValidation.data?.status}`,
          );
        }
      });
      return acc;
    }, previousValidations);
  }

  const hasValidations = !!previousValidations?.length;

  return (
    <RerunAiIndicator>
      <div className="vai-text-orange-400 vai-font-semibold vai-flex vai-items-center vai-gap-1 vai-mr-4">
        <BatchPredictionSharp className="warning vai-w-5 vai-h-5" />
        Updated AI
      </div>

      <Tooltip
        title={
          <span>
            <Trans>
              This visit&apos;s radiographs have been re-analyzed with the
              latest AI.{' '}
              {previousValidations && hasValidations && (
                <span>
                  Here are your user validations saved from the prior evaluation
                  of this radiograph:
                  <br /> {previousValidations.join(' • ')}
                </span>
              )}
            </Trans>
          </span>
        }
      >
        <InfoOutlined className="info" />
      </Tooltip>
    </RerunAiIndicator>
  );
};

export const RerunAIModal = ({
  isVDAPracticeWithNonVDAAnalysis,
  estimatedVisitId,
  images,
  selectedPatientId,
}: {
  isVDAPracticeWithNonVDAAnalysis: boolean;
  estimatedVisitId: number;
  images: ImageObject[];
  selectedPatientId: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const { data } = usePreferences();
  const imageAdjustmentVersion = data?.adjustmentVersion;

  const isMatchingAdjustmentVersion = (sharpenedPath?: string) => {
    if (!sharpenedPath || !imageAdjustmentVersion) {
      return true;
    }
    return sharpenedPath.search(imageAdjustmentVersion) === -1 ? false : true;
  };

  const shouldRerunAi = images?.some(
    (image) => !isMatchingAdjustmentVersion(image?.sharpenedPath),
  );

  const reRunImages = images
    .map((image: ImageObject) => {
      return {
        imageId: image.imageId,
        rotation: 0,
      };
    })
    .filter((image: { imageId: string }) => !!image.imageId);

  return (
    <>
      <VideaButton onClick={handleOpen}>Open Modal</VideaButton>
      <Dialog open={isOpen} onClose={handleClose}>
        <DialogTitle>Rerun AI Options</DialogTitle>
        <DialogContent>
          {isVDAPracticeWithNonVDAAnalysis && (
            <RerunAiIndicator>
              <div className="vai-text-orange-400 vai-font-semibold vai-flex vai-items-center vai-gap-1 vai-mr-4">
                <BatchPredictionSharp className="warning vai-w-5 vai-h-5" />
                Rerun to update rotation
              </div>

              <Tooltip title="AI results are not from latest model. Please rerun AI for latest analysis.">
                <InfoOutlined className="info" />
              </Tooltip>
              <RerunAiButton
                estimatedVisitId={estimatedVisitId}
                images={reRunImages}
                selectedPatientId={selectedPatientId}
              />
            </RerunAiIndicator>
          )}
          {shouldRerunAi && (
            <RerunAiIndicator>
              <div className="vai-text-orange-400 vai-font-semibold vai-flex vai-items-center vai-gap-1 vai-mr-4">
                <BatchPredictionSharp className="warning vai-w-5 vai-h-5" />
                Previous AI Model
              </div>

              <Tooltip title="AI results are not from latest model. Please rerun AI for latest analysis.">
                <InfoOutlined className="info" />
              </Tooltip>
              <RerunAi
                estimatedVisitId={estimatedVisitId}
                images={images}
                selectedPatientId={selectedPatientId}
              />
            </RerunAiIndicator>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} aria-label="Close Rerun AI Dialog">Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
